// probably timing problem with lit: https://github.com/lit/lit/blob/7453e365000e6a289c139cf7e175a4742296333d/packages/reactive-element/src/css-tag.ts#L10
// if lit is loaded before style sheet it cannot handle CSSStyleSheets from AdornisCSS
import 'construct-style-sheets-polyfill'; // THIS HAS TO BE THE FIRST IMPORT FOR FIREFOX TO WORK CORRECTLY

import { logger } from '@adornis/base/logging.js';
import { BaseQLClient } from '@adornis/baseql/client/baseqlClient.js';
import { subscribeCurrentUser } from '@adornis/users/client/currentUser.js';
import { createElement } from 'react';
import { createRoot } from 'react-dom/client';
import '../db/index.js';
import '../i18n.js';
import { getGuestUserID } from './lib/guest-user-id.js';
import { App } from './App.js';
import './tailwind.css' with { type: 'tailwind' };
// Self-hosted fonts: loading them from Google Fonts would send every visitor's IP address to Google. Imported here
// rather than in tailwind.css, because the tailwind plugin inlines @imports and esbuild then can't resolve their url()s.
import '@fontsource/inter/400.css';
import '@fontsource/inter/500.css';
import '@fontsource/inter/600.css';
import '@fontsource/poppins/400.css';
import '@fontsource/poppins/700.css';

// The first connect is covered by the subscribeCurrentUser() call further down.
let hasConnectedBefore = false;

BaseQLClient.initDefault({
  getInitPayload: async (sessionID) => ({
    // Always forward the sessionID (it lives in localStorage) so logged-in users are not logged
    // out on every new tab or page refresh. The guestUserID lives in sessionStorage so the guest
    // identity is ephemeral per tab — these are independent and need not be in sync.
    sessionID,
    guestUserID: getGuestUserID(),
  }),
  // The server drops the connection when this session was changed elsewhere, e.g. signed out by a password reset on
  // another device. The reconnected session has no user, but the subscription below was set up once for the old
  // one, so the UI would keep showing the account until a reload. Every reconnect re-reads who the session belongs
  // to: one query, and no reliance on which terminate reason the server sent.
  onConnect: () => {
    if (hasConnectedBefore) {
      subscribeCurrentUser().catch(err => logger.warn({ err }, 'subscribeCurrentUser after reconnect failed'));
    }
    hasConnectedBefore = true;
  },
});

// Apply saved theme before first render to avoid flash of wrong theme.
// This mirrors the logic in settings-page.tsx but runs synchronously at startup.
(function applyInitialTheme() {
  const savedTheme = localStorage.getItem('theme') || 'system';
  const html = document.documentElement;
  html.classList.remove('light', 'dark');
  if (savedTheme === 'system') {
    html.classList.add(window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light');
  } else {
    html.classList.add(savedTheme);
  }
})();

// Subscribe to current user session — fills the currentUser observable
// so that the React useCurrentUser hook can reactively track login state.
subscribeCurrentUser().catch(err => logger.warn({ err }, 'subscribeCurrentUser failed'));

const container = document.getElementById('root');
if (container) {
  const root = createRoot(container);
  root.render(createElement(App));
}
